import React, { useState, useContext, type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
import { SERVICE_DESK_PROJECT } from '@atlassian/jira-common-constants/src/project-types';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import CustomRolesModalAsync from '@atlassian/jira-project-custom-roles/src/async.tsx';
import UpFlowTouchpoint from '@atlassian/jira-up-flow-touchpoint/src/main.tsx';
import ReloadPageContext from '../../../controllers/reload-page-context';
import type { ReloadContext } from '../../../controllers/reload-page-context/types';
import { customRoleInteractionMetrics } from '../../../utils/performance-analytics';
import messages from './messages';
import type { Props } from './types';

export const ManageRoles = (props: Props) => {
	const {
		hasPermissionsGate,
		isFetching,
		isError,
		projectType,
		intl: { formatMessage },
	} = props;
	const [isCustomRolesAppModalOpen, setIsCustomeRolesModalOpen] = useState<boolean>(false);
	const getFreeMessageBasedOnProjectType = (): MessageDescriptor => {
		if (projectType === SERVICE_DESK_PROJECT) {
			return messages.manageRolesJsmFreeTooltip;
		}
		return messages.manageRolesJswFreeTooltip;
	};
	const reloadContext = useContext<ReloadContext>(ReloadPageContext);

	const [isTouchpointOpen, setIsTouchpointOpen] = useState<boolean>(false);

	const onTouchpointOpen = (): void => {
		setIsTouchpointOpen(true);
	};
	const onTouchpointClose = (): void => {
		setIsTouchpointOpen(false);
	};

	const toggleCustomRoleApp = (e: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
		setIsCustomeRolesModalOpen(!isCustomRolesAppModalOpen);
		fireUIAnalytics(analyticsEvent, 'manageRolesButton');
		customRoleInteractionMetrics.start();
	};

	const onStopPerfMetric = () => customRoleInteractionMetrics.stop();

	const onClose = (
		e: SyntheticEvent,
		analyticsEvent: UIAnalyticsEvent,
		shouldAccessPageBeRefreshed: boolean,
	): void => {
		setIsCustomeRolesModalOpen(!isCustomRolesAppModalOpen);
		if (shouldAccessPageBeRefreshed) {
			reloadContext.reloadPage();
		}
	};

	const renderManageRoleButton = () => (
		<div data-testid="users-and-roles.ui.header.manage-roles.button">
			<Button
				appearance="default"
				onClick={toggleCustomRoleApp}
				isDisabled={isFetching || isError || hasPermissionsGate}
			>
				{formatMessage(messages.manageRoles)}
			</Button>
			{isCustomRolesAppModalOpen && (
				// @ts-expect-error - TS2741 - Property 'FetchRolesAndPermissionsService' is missing in type '{ isOpen: true; onClose: (syntheticEvent: SyntheticEvent<Element, Event>, event: UIAnalyticsEventInterface, shouldAccessPageBeRefreshed: boolean) => void; onStopPerfMetric: () => boolean; }' but required in type '{ FetchRolesAndPermissionsService: ComponentType<Props>; }'.
				<CustomRolesModalAsync
					onStopPerfMetric={onStopPerfMetric}
					isOpen={isCustomRolesAppModalOpen}
					onClose={onClose}
				/>
			)}
		</div>
	);

	const freeText = formatMessage(getFreeMessageBasedOnProjectType());

	return (
		<UpFlowTouchpoint
			isOpen={hasPermissionsGate && isTouchpointOpen}
			actionSubjectId="permissionGatedManageRole"
			feature="permission"
			touchpointId="projectSettingsAccessPermissionGatedManageRole"
			internationalizedMessage={freeText}
			placement="bottom-end"
			onOpen={onTouchpointOpen}
			onClose={onTouchpointClose}
		>
			{renderManageRoleButton()}
		</UpFlowTouchpoint>
	);
};

export default injectIntl(ManageRoles);
